import { ref, computed } from "vue";
import SearchService from "../services/searchService.js";

const STORAGE_KEY = "v2-recent-searches";
const MAX_RECENT = 10;

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? []; }
  catch { return []; }
}

function saveToStorage(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

// Module-level singleton — TopBar's search box and the Search page both call
// useSearch(); they need to share one query/result set, not get independent
// instances (which they would if this state lived inside the function body).
const activeQuery = ref("");
const inputValue = ref("");
const isSearching = ref(false);
const recentSearches = ref(loadRecent());

const projectResults = ref([]);
const documentResults = ref([]);
const textResults = ref([]);
const annotationResults = ref([]);

let unsubscribe = null;

function addRecent(q) {
  const filtered = recentSearches.value.filter((r) => r.query !== q);
  recentSearches.value = [{ query: q, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
  saveToStorage(recentSearches.value);
}

function removeRecent(q) {
  recentSearches.value = recentSearches.value.filter((r) => r.query !== q);
  saveToStorage(recentSearches.value);
}

function clearRecent() {
  recentSearches.value = [];
  localStorage.removeItem(STORAGE_KEY);
}

function search(q) {
  const query = (q ?? inputValue.value).trim();
  if (!query) return;

  unsubscribe?.();
  activeQuery.value = query;
  inputValue.value = query;
  isSearching.value = true;
  projectResults.value = [];
  documentResults.value = [];
  textResults.value = [];
  annotationResults.value = [];

  addRecent(query);

  // Built fresh per search (not a module-level singleton) so it always
  // picks up the current accessToken — the WS link's auth is captured once
  // at construction time, so a stale singleton built before login completed
  // would be stuck unauthenticated for the whole session.
  const searchService = new SearchService();
  unsubscribe = searchService.subscribe(query, {
    onResult: (result) => {
      switch (result.type) {
        case "project":    projectResults.value.push(result.data); break;
        case "document":   documentResults.value.push(result.data); break;
        case "text":       textResults.value.push(result.data); break;
        case "annotation": annotationResults.value.push(result.data); break;
      }
    },
    onError: (error) => {
      console.error("Search subscription failed:", error);
      isSearching.value = false;
    },
    onComplete: () => { isSearching.value = false; },
  });
}

function resetSearch() {
  unsubscribe?.();
  unsubscribe = null;
  activeQuery.value = "";
  inputValue.value = "";
  isSearching.value = false;
  projectResults.value = [];
  documentResults.value = [];
  textResults.value = [];
  annotationResults.value = [];
}

export function useSearch() {
  const totalResults = computed(() =>
    projectResults.value.length + documentResults.value.length +
    textResults.value.length + annotationResults.value.length
  );

  const hasResults = computed(() => totalResults.value > 0);
  const hasSearched = computed(() => !!activeQuery.value);

  return {
    inputValue, activeQuery, isSearching, hasSearched, hasResults,
    recentSearches, projectResults, documentResults, textResults, annotationResults,
    search, resetSearch, removeRecent, clearRecent,
  };
}
